import { type ErrorInfo, PureComponent, type ReactNode } from "react";

import { Column, Button, Text, ButtonGroup, Heading } from "@hightouch/htui";
import { PageSpinner } from "src/components/loading";
import {
  captureReactErrorBoundaryError,
  isChunkLoadError,
} from "src/lib/monitoring";
import {
  extractAssetUrl,
  recoverAndReload,
  reloadForChunkError,
  reloadForDeploySkew,
} from "src/lib/chunk-reload";
import { isDeploySkewError } from "src/lib/chunk-retry";
import {
  RELOAD_ENABLED,
  hasAppVersionChanged,
  reloadOnceForVersionChange,
  clearVersionReloadFlag,
} from "src/components/router/force-reload-utils";

import * as analytics from "src/lib/analytics";
import { newPylonMessage } from "src/lib/pylon";

interface Props {
  children: ReactNode;
  /** Called once the crash screen replaces the children. */
  onCrash?: () => void;
}

interface State {
  isCrashed: boolean;
  isReloadPending: boolean;
  /** Asset URL of the chunk that crashed, if any — used by the reload button. */
  failedAssetUrl: URL | null;
}

export class ErrorBoundary extends PureComponent<Props, State> {
  contactSupport = () => {
    analytics.track("Contact support clicked", {});

    newPylonMessage("");
  };

  override state: State = {
    isCrashed: false,
    isReloadPending: false,
    failedAssetUrl: null,
  };

  override componentDidMount() {
    // Delay clearing the version-reload guard so lazy route chunks have time
    // to load or fail. If a version-change crash fires during this window the
    // flag is still set, so we show the crash UI instead of looping. (The
    // chunk-reload budget is keyed to the build SHA and needs no clear here —
    // see reloadForChunkError.)
    setTimeout(() => {
      clearVersionReloadFlag();
    }, 5_000);
  }

  override render() {
    if (this.state.isReloadPending) {
      // The auto-reload may be deferred a few seconds by the pre-reload cache
      // overwrite — show a spinner rather than a blank pane meanwhile.
      return <PageSpinner />;
    }

    if (!this.state.isCrashed) {
      return this.props.children;
    }

    return (
      <Column width="100%" p={20} align="center" gap={4}>
        <svg
          fill="none"
          height="48"
          stroke="black"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="48"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
          <path d="M9 9H9.01" />
          <path d="M15 9H15.01" />
          <path d="M16 14C16 14 12 14 12 14C12 14 8 14 8 14">
            <animate
              attributeName="d"
              begin="400ms"
              calcMode="spline"
              dur="1s"
              fill="freeze"
              keySplines="0.22 1 0.36 1"
              values="M16 14C16 14 12 14 12 14C12 14 8 14 8 14; M16 16C16 16 14.5 14 12 14C9.5 14 8 16 8 16"
            />
          </path>
        </svg>

        <Heading size="lg">Something went wrong…</Heading>
        <Text>
          An error has occurred and we certainly didn&apos;t expect it.
          <br />
          We&apos;ve been notified and will fix it as soon as possible.
        </Text>

        <ButtonGroup size="lg">
          <Button variant="secondary" onClick={this.contactSupport}>
            Contact support
          </Button>

          <Button onClick={this.reload}>Reload page</Button>
        </ButtonGroup>
      </Column>
    );
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // We think these errors are caused by a third party library or a browser extension. It seems to be safe to carry on.
    if (
      error.message?.includes("removeChild") &&
      error.message?.includes(
        "The node to be removed is not a child of this node",
      )
    ) {
      console.warn("removeChild error caught, forcing re-render:", error);
      // Force a re-render to recover from any inconsistent state
      this.forceUpdate();
      return;
    }

    // Deploy skew (a chunk 404 / SPA-HTML at an asset path) takes a single
    // URL-preserving reload to the fresh build, off the poison budget and
    // guarded to once per build. Check it before the generic chunk path since a
    // skew error also matches isChunkLoadError.
    if (isDeploySkewError(error) && reloadForDeploySkew(error)) {
      this.setState({ isReloadPending: true });
      return;
    }

    // On deploy-time cache invalidation, stale HTML may reference deleted chunks.
    // Auto-reload once to fetch the fresh chunk manifest; if it still fails
    // after reloading, fall through to the crash UI to avoid an infinite loop.
    if (
      !isDeploySkewError(error) &&
      isChunkLoadError(error) &&
      reloadForChunkError(error)
    ) {
      this.setState({ isReloadPending: true });
      return;
    }

    // A stale bundle whose bootstrap query references a field a newer schema
    // dropped will crash here. If a newer build has shipped since this tab
    // loaded, reload once to pick it up instead of stranding the user on the
    // crash screen. <ForceReload> can't do this — it lives in the subtree this
    // boundary just unmounted.
    if (
      RELOAD_ENABLED &&
      hasAppVersionChanged() &&
      reloadOnceForVersionChange()
    ) {
      return;
    }

    // For all other errors, crash the app. Capture the failed asset URL (null
    // for non-chunk crashes) so the reload button can bust a poisoned cache
    // entry instead of re-serving it via a bare reload.
    this.setState({
      isCrashed: true,
      failedAssetUrl: extractAssetUrl(error),
    });
    this.props.onCrash?.();

    console.error(error);
    console.error(errorInfo.componentStack);
    captureReactErrorBoundaryError(error, errorInfo);
  }

  reload = () => {
    void recoverAndReload(this.state.failedAssetUrl);
  };
}
